import React from 'react';
import './Card.css';

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Raises the card with elevation shadow. */
  raised?: boolean;
  /** Enables hover/focus states. Pair with onClick or role="button". */
  interactive?: boolean;
  /** Uses tighter padding for dashboard widgets. */
  compact?: boolean;
}

/**
 * Card container. Use Card.Header / Card.Body / Card.Footer for the standard
 * three-zone layout, or render arbitrary children for custom compositions.
 */
export const Card = React.forwardRef<HTMLDivElement, CardProps>(function Card(
  { raised, interactive, compact, className, children, ...rest },
  ref
) {
  const classes = [
    'bso-card',
    raised ? 'bso-card--raised' : '',
    interactive ? 'bso-card--interactive' : '',
    compact ? 'bso-card--compact' : '',
    className ?? ''
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div {...rest} ref={ref} className={classes}>
      {children}
    </div>
  );
}) as React.ForwardRefExoticComponent<
  CardProps & React.RefAttributes<HTMLDivElement>
> & {
  Header: typeof CardHeader;
  Body: typeof CardBody;
  Footer: typeof CardFooter;
  Title: typeof CardTitle;
  Subtitle: typeof CardSubtitle;
};

export interface CardHeaderProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
}

function CardHeader({ title, subtitle, actions, children, className, ...rest }: CardHeaderProps) {
  return (
    <div {...rest} className={['bso-card__header', className ?? ''].filter(Boolean).join(' ')}>
      {children ?? (
        <div>
          {title && <CardTitle>{title}</CardTitle>}
          {subtitle && <CardSubtitle>{subtitle}</CardSubtitle>}
        </div>
      )}
      {actions && <div>{actions}</div>}
    </div>
  );
}

function CardBody({ className, ...rest }: React.HTMLAttributes<HTMLDivElement>) {
  return <div {...rest} className={['bso-card__body', className ?? ''].filter(Boolean).join(' ')} />;
}

function CardFooter({ className, ...rest }: React.HTMLAttributes<HTMLDivElement>) {
  return <div {...rest} className={['bso-card__footer', className ?? ''].filter(Boolean).join(' ')} />;
}

function CardTitle({ className, ...rest }: React.HTMLAttributes<HTMLHeadingElement>) {
  return <h3 {...rest} className={['bso-card__title', className ?? ''].filter(Boolean).join(' ')} />;
}

function CardSubtitle({ className, ...rest }: React.HTMLAttributes<HTMLParagraphElement>) {
  return <p {...rest} className={['bso-card__subtitle', className ?? ''].filter(Boolean).join(' ')} />;
}

Card.Header = CardHeader;
Card.Body = CardBody;
Card.Footer = CardFooter;
Card.Title = CardTitle;
Card.Subtitle = CardSubtitle;
